import React, { useState, useEffect, useRef } from 'react';
// Import necessary icons from lucide-react and react-icons/fa
import { Menu, X, ArrowRight, Zap, CheckCircle, Lightbulb, TrendingUp, Cpu, Users, Code, Repeat, Server, DollarSign, Eye, Instagram, Mail, Phone, MapPin, Facebook, Linkedin, MessageCircle, ChevronDown } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';


const services = [
    {
      id: 'dev',
      title: 'Website Design and Development',
      description: 'At Vyxion Labs, we craft custom websites that combine stunning design with seamless functionality. Our expert team ensures your site engages visitors and drives real business results.',
      features: ['Custom Web Designs', 'SEO Optimization', 'Responsive Design', 'Security'],
      image: '/web_design.jpg',
      gradient: 'from-cyan-400 to-blue-500',
      cursorColor: 'rgba(34, 211, 238, 0.4)',
      hoverCursorColor: 'rgba(59, 130, 246, 0.6)',
      link: '/web-development',
    },
    {
      id: 'revamp',
      title: 'Website Revamp',
      description: 'Are you looking to give your website a fresh new look? Discover the benefits of our professional Website Revamp service. From enhanced functionality to improved user experience, we help you maximize your conversions.',
      features: ['Modern UI/UX Design', 'Performance Boost', 'Mobile Optimization', 'Brand Refresh'],
      image: '/web_revamp.jpg',
      gradient: 'from-purple-400 to-pink-500',
      cursorColor: 'rgba(168, 85, 247, 0.4)',
      hoverCursorColor: 'rgba(236, 72, 153, 0.6)',
      link: '/website-revamp',
    },
    {
      id: 'hosting',
      title: 'Server Hosting & Maintenance',
      description: 'Reliable, secure hosting solutions with 24/7 monitoring and expert maintenance to keep your site running flawlessly.',
      features: ['24/7 Server Monitoring', 'Security Updates', 'Content Revision', 'Technical Support'],
      image: '/server_hosting.jpg',
      gradient: 'from-emerald-400 to-teal-500',
      cursorColor: 'rgba(52, 211, 153, 0.4)',
      hoverCursorColor: 'rgba(20, 184, 166, 0.6)',
      link: '/hosting-maintenance',
    },
];


const faqItems = [
    {
        id: 1,
        question: "How long does it take to build a website?",
        answer: "The timeline depends on the complexity and scope of the project. A standard informational website typically takes 4-6 weeks from consultation to launch, while more complex sites with custom features may take longer. We'll provide a detailed timeline after the initial consultation."
    },
    {
        id: 2,
        question: "Will I own the website?",
        answer: "Yes definitely! We only do the Website Design & Development and Hosting but the Website rights and the data belong to you."
    },
    {
        id: 3,
        question: "How much does a website cost?",
        answer: "Our pricing is tailored to the specific needs of your project. Factors like design complexity, number of pages, custom functionality (e.g. Integrations), and ongoing maintenance are all considered. Book a free consultation for an accurate quote."
    },
   
    {
        id: 4,
        question: "Are there renewal fees?",
        answer: "Yes. To ensure that the website is hosted on the server and the domain is maintained, a monthly fee will be charged as part of our Server Hosting & Maintenance service."
    
    },
    {
        id: 5,
        question: "What is the payment process?",
        answer: "There will be a 50% deposit required after the intial UI designs have been approved and before the final deployment of the website, the remaining amount will be collected."
    
    },
    {
        id: 6,
        question: "Are there any hidden fees or extra costs?",
        answer: "No. Our proposals are detailed and all-inclusive for the defined scope. Any requested changes outside the original scope are only made when they are approved by you beforehand."
    
    },
    {
        id: 7,
        question: "Do you offer website maintenance and support?",
        answer: "Yes, we offer comprehensive hosting and maintenance packages. This includes 24/7 monitoring, security updates, regular backups, technical support, and content revision to ensure your site is always running optimally and securely."
    },
    {
        id: 8,
        question: "What is the process for a website revamp?",
        answer: "Our revamp process begins with an audit of your current site to identify pain points. We then focus on modern UI/UX design, performance optimization, and updating your site's technology stack without losing valuable existing content. The goal is a seamless transition to a better-performing site."
    },
    {
        id: 9,
        question: "What if I don't like the initial design?",
        answer: "Our process includes revisions built into the contract. We start with an intial design and you are highly encouraged to show us mood boards or other materials to ensure we align on aesthetics early on. We won't proceed to developing until you are pleased with the design."
    }
    
];
// -------------------------------------------------------------------------



const FAQItem = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="border-b border-slate-200 py-6">
            <button
                className="flex justify-between items-center w-full text-left focus:outline-none"
                onClick={() => setIsOpen(!isOpen)}
            >
                <h3 className="text-xl font-semibold text-slate-800 hover:text-cyan-600 transition-colors">
                    {item.question}
                </h3>
                <ChevronDown className={`w-6 h-6 text-slate-500 transition-transform duration-300 ${isOpen ? 'transform rotate-180 text-cyan-500' : ''}`} />
            </button>
            <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}
            >
                <p className="text-slate-600 text-lg pr-8">{item.answer}</p>
            </div>
        </div>
    );
};



const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-12 border-b border-slate-700 pb-12">
                    {/* Column 1: Brand & Contact */}
                    <div className="col-span-2 md:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        
                        <div className="flex space-x-4 pt-2">
                        <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                        <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                            
                        </div>
                    </div>

                    {/* Column 2: Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            {/* Link to this page */}
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                            
                        </ul>
                    </div>

                    {/* Column 3: Services */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Column 4: Get In Touch */}
                    <div className="space-y-4 col-span-2 md:col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors">vyxionlabs.info@gmail.com</a>
                            </li>
            
                            
                        </ul>
                    </div>
                </div>
                
                {/* LEGAL LINKS */}
                <div className="flex justify-center md:justify-start space-x-6 mt-8">
                    
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>

                {/* Copyright */}
                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};
// -------------------------------------------------------------

// --- The Main FAQ Page Component ---
export default function FAQPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  
  const servicesDropdownRef = useRef(null); 

  // Functions for Services Dropdown
  const handleServicesClick = (e) => {
    e.stopPropagation(); 
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to toggle Contact Screen Overlay
  const handleMenuClick = () => {
    setIsContactPopupOpen(!isContactPopupOpen);
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 text-slate-900 min-h-screen antialiased relative overflow-x-hidden">
      <style>{`
        /* Minimal styles copied for header/menu functionality */
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(8px);
            z-index: 9999; 
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .modal-overlay.open {
            opacity: 1;
            visibility: visible;
        }
        .modal-content {
            position: fixed;
            top: 0;
            right: 0;
            width: 90%;
            max-width: 400px;
            height: 100%;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(30px);
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
            transform: translateX(100%);
            transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
            overflow-y: auto;
            padding: 3rem 1.5rem;
        }
        .modal-overlay.open .modal-content {
            transform: translateX(0);
        }
      `}</style>

      
  

      
      <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox Layout */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                   <div className="flex flex-col p-2">
                       <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Design & Development
                       </a>
                       <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Revamp
                       </a>
                       <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Server Hosting & Maintenance
                       </a>
                   </div>
               </div>
               
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">About Us</a>
                <a href="/ourworks" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Our Works</a>
                <a href="/faq" className="text-neutral-900 font-bold cursor-pointe">FAQ</a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-900 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Contact Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100">
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-400" onClick={handleMenuClick}>About Us</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
            <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
           
         
        </div>
      </div>

      <main>
        {/* --- Compact FAQ Header/Title Section (REPLACED HERO) --- */}
        <section className="relative pt-12 pb-4 md:pt-6 md:pb-6 px-6 bg-white/70 backdrop-blur-sm border-b border-slate-200">
            <div className="max-w-7xl mx-auto w-full relative z-10 text-left space-y-4">
                <h1 className="text-4xl md:text-6xl font-black leading-tight">
                    <span className="block bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                        Frequently Asked Questions
                    </span>
                </h1>
                <p className="text-lg text-slate-600 max-w-4xl leading-relaxed">
                    Find quick answers about our web development process, pricing, timelines, and maintenance services. If your question isn't listed, please book a free consultation!
                </p>
            </div>
        </section>

        {/* --- FAQ Accordion Section --- */}
        {/* --- FAQ Accordion Section --- */}
        <section className="relative py-10 px-6">
            <div className="max-w-5xl mx-auto p-8 bg-white rounded-3xl shadow-2xl border border-slate-200">
                <div className="space-y-4">
                    {faqItems.map(item => (
                        <FAQItem key={item.id} item={item} />
                    ))}
                </div>
            </div>
        </section>
        
        {/* --- Call to Action Footer Section (Simplified CTA Banner) --- */}
        <section className="relative py-20 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col items-center text-center space-y-8 p-10 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-purple-500/10 shadow-xl border border-slate-200">
                    <h2 className="text-3xl md:text-5xl font-black leading-tight">
                        Didn't find your answer? <br />
                        <span className="font-serif italic text-cyan-600">Let’s talk!</span>
                    </h2>

                    <a
                        href="/consult"
                        className="inline-flex items-center gap-2 px-8 py-3 bg-orange-500 text-white font-bold rounded-xl shadow-lg hover:bg-orange-600 transition-colors"
                    >
                        Book Your Free Consult <ArrowRight className="w-5 h-5" />
                    </a>
                </div>
            </div>
        </section>
        
        {/* --- FOOTER SECTION --- */}
        <FooterSection />
      </main>
    </div>
  );
}